import { useMutation, useQuery } from '@apollo/client';
import { produce } from 'immer';
import { useEffect } from 'react';
import { v4 } from 'uuid';

import { cacheUtils } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import {
  useAppointmentActivity,
  useAppointmentMode,
  useIsAppointmentReadonly,
} from '@eluve/frontend-appointment-hooks';
import { DbEnums } from '@eluve/graphql-types';
import { FragmentOf } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';
import { usePrevious } from '@eluve/utility-hooks';

import {
  addAppointmentBillingCodeMutation,
  appointmentBillingCodeRecommendationsFragment,
  appointmentBillingCodesFragment,
  getAppointmentBillingCodesQuery,
  medicalCodeFragment,
  removeBillingCodeMutation,
} from './AppointmentBillingCodes.operations';

export type MedicalCodeType = Omit<
  FragmentOf<typeof medicalCodeFragment>,
  '__typename'
> & {
  price?: number | null;
  modifiers?: string[] | null;
  userGroupCodeConfigId?: string | null;
};

export const useEditBillingCodes = ({
  tenantId,
  appointmentId,
}: {
  tenantId: string;
  appointmentId: string;
}) => {
  const toast = useToast();
  const analytics = useAnalytics('billing_codes_recommendation');

  const userId = useUserIdFromSession();

  const { appointmentLlmJobs } = useAppointmentActivity();

  const isReadonly = useIsAppointmentReadonly();
  const appointmentMode = useAppointmentMode();
  const isAudioCopilotChart =
    appointmentMode === DbEnums.AppointmentMode.AUDIO_COPILOT;

  const shouldTrackBillingCodeRecommendations = !isReadonly;

  const billingCodeJob = appointmentLlmJobs.find(
    (job) => job.llmOutputType === 'BILLING_CODE_RECOMMENDATION',
  );

  const billingCodesJobStatus = billingCodeJob?.jobStatus;

  const previousBillingCodesJobStatus = usePrevious(billingCodesJobStatus);

  const { data, refetch } = useQuery(getAppointmentBillingCodesQuery, {
    variables: {
      tenantId,
      appointmentId,
    },
    notifyOnNetworkStatusChange: true,
  });

  const previousData = usePrevious(data);

  useEffect(() => {
    if (
      data !== previousData &&
      shouldTrackBillingCodeRecommendations &&
      data?.appointmentsByPk?.billing_code_recommendations
    ) {
      analytics.capture('billing_code_list_show', {
        tenantId,
        userId,
        appointmentId,
        totalCodes: data.appointmentsByPk.billing_code_recommendations.length,
      });
    }
  }, [
    data,
    previousData,
    shouldTrackBillingCodeRecommendations,
    analytics,
    tenantId,
    userId,
    appointmentId,
  ]);

  // If a recommendation job finishes since we fetched, we should trigger a refetch
  useEffect(
    function refetchManualChartCodeRecommendationsOnJobCompletion() {
      if (
        previousBillingCodesJobStatus === DbEnums.WorkerStatus.IN_PROGRESS &&
        billingCodesJobStatus === DbEnums.WorkerStatus.COMPLETED
      ) {
        refetch();
      }
    },
    [
      refetch,
      isAudioCopilotChart,
      billingCodesJobStatus,
      previousBillingCodesJobStatus,
    ],
  );

  const billingCodes =
    data?.appointmentsByPk?.billingCodes.filter((c) =>
      Boolean(c.medical_code),
    ) ?? [];
  const recommendations =
    data?.appointmentsByPk?.billing_code_recommendations ?? [];

  const [removeBillingCode] = useMutation(removeBillingCodeMutation, {
    onError: () => toast.error('Failed to remove billing code'),
    onCompleted: (data) => {
      if (shouldTrackBillingCodeRecommendations) {
        if (data.updateAppointmentBillingCodeRecommendations?.returning) {
          analytics.capture('billing_code_update', {
            tenantId,
            userId,
            appointmentId,
          });
        }
        if (data.deleteAppointmentBillingCodesByPk?.id) {
          analytics.capture('billing_code_remove', {
            tenantId,
            userId,
            appointmentId,
          });
        }
      }
    },
  });

  const [addBillingCode] = useMutation(addAppointmentBillingCodeMutation, {
    onError: () => toast.error('Failed to add billing code'),
    onCompleted: (data) => {
      if (shouldTrackBillingCodeRecommendations) {
        if (data.insertAppointmentBillingCodesOne) {
          analytics.capture('billing_code_add', {
            tenantId,
            userId,
            appointmentId,
          });
        }
      }
    },
  });

  const onRemoveBillingCode = async (medicalCode: MedicalCodeType) => {
    const billingCode = billingCodes.find((billingCode) => {
      return billingCode.medical_code?.id === medicalCode.id;
    });

    if (!billingCode) {
      toast.error('Could not find the billing code to remove');
      return;
    }

    const billingCodeRecommendationIdToUpdate = recommendations.find(
      (code) => code.medical_code?.id === medicalCode.id,
    )?.id;

    await removeBillingCode({
      variables: {
        appointmentBillingCodeId: billingCode.id,
        appointmentBillingCodeRecommendationId:
          billingCodeRecommendationIdToUpdate ?? null,
        skipUpdate: !billingCodeRecommendationIdToUpdate,
      },
      optimisticResponse: () => {
        return {
          deleteAppointmentBillingCodesByPk: {
            __typename: 'AppointmentBillingCodes' as const,
            id: billingCode.id,
          },
          updateAppointmentBillingCodeRecommendations: {
            returning: billingCodeRecommendationIdToUpdate
              ? [
                  {
                    id: billingCodeRecommendationIdToUpdate,
                    __typename:
                      'AppointmentBillingCodeRecommendations' as const,
                  },
                ]
              : [],
          },
        };
      },
      update: (_cache) => {
        if (billingCodeRecommendationIdToUpdate) {
          cacheUtils.updateFragment(
            {
              fragment: appointmentBillingCodeRecommendationsFragment,
              key: {
                id: appointmentId,
              },
            },
            (existing) => {
              if (!existing) {
                return existing;
              }
              return produce(existing, (draft) => {
                const index = draft.billing_code_recommendations.findIndex(
                  (rec) => rec.id === billingCodeRecommendationIdToUpdate,
                );
                if (index !== -1 && draft.billing_code_recommendations[index]) {
                  draft.billing_code_recommendations[index].isFinal = false;
                }
              });
            },
          );
        }

        cacheUtils.updateFragment(
          {
            fragment: appointmentBillingCodesFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }

            return produce(existing, (draft) => {
              // Remove the billing code from the list
              if (medicalCode) {
                const idx = draft.billingCodes.findIndex(
                  (c) => c.medical_code?.id === medicalCode.id,
                );
                if (idx !== -1) {
                  draft.billingCodes.splice(idx, 1);
                }
              }

              // Remove any links for the billing code
              draft.billingCodes = draft.billingCodes.map((code) => ({
                ...code,
                linked_codes: code.linked_codes.filter(
                  (link) =>
                    link.sourceAppointmentBillingCodeId !== billingCode.id &&
                    link.targetAppointmentBillingCodeId !== billingCode.id,
                ),
              }));
            });
          },
        );
      },
    });
  };

  const onAddBillingCode = async (
    medicalCode: MedicalCodeType & { userGroupCodeConfigId: string | null },
  ) => {
    await addBillingCode({
      variables: {
        appointmentId,
        billingCodeId: medicalCode.id ?? null,
        price: medicalCode.price ?? null,
        quantity: 1,
        modifiers: medicalCode.modifiers ?? null,
        userGroupCodeConfigId: medicalCode?.userGroupCodeConfigId ?? null,
      },
      optimisticResponse: ({ price, quantity, modifiers }) => ({
        insertAppointmentBillingCodesOne: {
          __typename: 'AppointmentBillingCodes' as const,
          id: v4(),
          price: price ?? null,
          quantity: quantity ?? null,
          description: medicalCode.description ?? null,
          modifiers: modifiers ?? null,
        },
      }),
      update: (_cache, { data }) => {
        cacheUtils.updateFragment(
          {
            fragment: appointmentBillingCodesFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            const updatedId = data?.insertAppointmentBillingCodesOne?.id;
            if (!existing || !updatedId) {
              return existing;
            }

            // Optimistically add the billing code to the list of accepted billing codes
            return produce(existing, (draft) => {
              draft.billingCodes.push({
                __typename: 'AppointmentBillingCodes',
                id: updatedId,
                price: data?.insertAppointmentBillingCodesOne?.price ?? null,
                quantity: 1,
                modifiers:
                  data?.insertAppointmentBillingCodesOne?.modifiers ?? null,
                linked_codes: [],
                description:
                  data?.insertAppointmentBillingCodesOne?.description ?? null,
                medical_code: {
                  __typename: 'MedicalCodes',
                  ...medicalCode,
                },
              });
            });
          },
        );
      },
    });
  };

  return {
    billingCodes,
    refetchBillingCodes: refetch,
    recommendations,
    onRemoveBillingCode,
    onAddBillingCode,
    tenantMedicalCodesExist: data?.tenantsByPk?.tenantMedicalCodesExist,
  };
};
